import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  FileText, 
  Receipt, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  DollarSign,
  Bell,
  TrendingUp,
  Truck,
  Send,
  Loader2
} from 'lucide-react';

export const MyTasksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState({
    pending_quotations: [],
    pending_timesheets: [],
    pending_invoices: [],
    overdue_payments: [],
    pending_follow_ups: [],
    my_commissions: { eligible: 0, pending: 0 },
    expiring_documents: []
  });
  const [expiringContracts, setExpiringContracts] = useState({ items: [], count: 0, expired_count: 0, expiring_soon_count: 0 });
  const [reminderLog, setReminderLog] = useState({ items: [], count: 0 });
  const [sendingReminders, setSendingReminders] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyTasks();
    loadExpiringContracts();
    loadReminderLog();
  }, []);

  const loadMyTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/my-tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExpiringContracts = async () => {
    try {
      const response = await api.get('/fleet/expiring-contracts?within_days=30');
      setExpiringContracts(response.data);
    } catch (error) {
      console.error('Failed to load expiring contracts:', error);
    }
  };

  const loadReminderLog = async () => {
    try {
      const response = await api.get('/fleet/contract-reminders/log?limit=10');
      setReminderLog(response.data);
    } catch (error) {
      console.error('Failed to load reminder log:', error);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await api.post('/fleet/contract-reminders/scan', { channels: ['email'] });
      const { fired, scanned, items = [] } = response.data;
      const renewals = items.filter((it) => it?.result?.renewal?.po_number);
      if (fired > 0) {
        const renewalMsg = renewals.length
          ? ` · ${renewals.length} draft renewal PO${renewals.length !== 1 ? 's' : ''} created`
          : '';
        toast.success(`Sent ${fired} reminder${fired !== 1 ? 's' : ''} (${scanned} scanned)${renewalMsg}`);
      } else {
        toast.info(`Scanned ${scanned} contracts — no new milestones to send. (Already-sent reminders are skipped.)`);
      }
      loadReminderLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const totalPendingTasks = 
    tasks.pending_quotations.length +
    tasks.pending_timesheets.length +
    tasks.pending_invoices.length +
    tasks.overdue_payments.length +
    tasks.pending_follow_ups.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="my-tasks-title">
            My Tasks
          </h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.full_name}
          </p>
        </div>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Pending Tasks</div>
          <div className="text-3xl font-bold text-amber-600" data-testid="total-pending">
            {totalPendingTasks}
          </div>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading your tasks...</div>
        </div>
      ) : (
        <>
          {/* Global Admin Alert: Supplier-rented contracts expiring within 30 days */}
          {(user?.role === 'admin' || user?.role === 'operations') && expiringContracts.count > 0 && (
            <Card
              className={`p-5 border-l-4 ${
                expiringContracts.expired_count > 0
                  ? 'border-l-red-500 bg-red-50/50'
                  : 'border-l-orange-500 bg-orange-50/50'
              }`}
              data-testid="expiring-contracts-banner"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    expiringContracts.expired_count > 0 ? 'bg-red-100' : 'bg-orange-100'
                  }`}>
                    <AlertTriangle className={`h-5 w-5 ${
                      expiringContracts.expired_count > 0 ? 'text-red-600' : 'text-orange-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Truck className="h-4 w-4" /> Supplier Rental Contracts Expiring
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="font-semibold text-red-700" data-testid="contracts-expired-count">
                        {expiringContracts.expired_count}
                      </span>{' '}
                      expired ·{' '}
                      <span className="font-semibold text-orange-700" data-testid="contracts-expiring-count">
                        {expiringContracts.expiring_soon_count}
                      </span>{' '}
                      expiring within 30 days
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSendReminders}
                    disabled={sendingReminders}
                    data-testid="send-reminders-button"
                  >
                    {sendingReminders ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="h-3.5 w-3.5 mr-1.5" /> Send Reminders</>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/fleet')} data-testid="view-expiring-contracts-button">
                    Review in Fleet
                  </Button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {expiringContracts.items.slice(0, 4).map((c) => (
                  <div
                    key={c.asset_id}
                    className="flex items-center justify-between text-xs bg-white/70 rounded px-3 py-2 border"
                    data-testid={`contract-row-${c.asset_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono font-semibold">{c.registration_number}</span>
                      <span className="text-muted-foreground truncate">
                        · {c.make} {c.model} · {c.supplier_name || 'Unassigned supplier'}
                      </span>
                    </div>
                    <Badge
                      className={
                        c.is_expired
                          ? 'bg-red-100 text-red-800'
                          : c.days_until_expiry <= 7
                            ? 'bg-red-100 text-red-800'
                            : 'bg-orange-100 text-orange-800'
                      }
                    >
                      {c.is_expired ? `Expired ${Math.abs(c.days_until_expiry)}d ago` : `${c.days_until_expiry}d left`}
                    </Badge>
                  </div>
                ))}
                {expiringContracts.count > 4 && (
                  <p className="text-xs text-muted-foreground pt-1">
                    +{expiringContracts.count - 4} more expiring contract{expiringContracts.count - 4 !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {reminderLog.count > 0 && (
                <details className="mt-3 text-xs" data-testid="reminder-log-toggle">
                  <summary className="cursor-pointer text-muted-foreground hover:text-slate-700 font-medium">
                    Reminder history ({reminderLog.count} recent)
                  </summary>
                  <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto" data-testid="reminder-log-list">
                    {reminderLog.items.map((r) => (
                      <div key={r.id} className="flex items-center justify-between bg-white/60 rounded px-2.5 py-1.5 border text-xs">
                        <div className="min-w-0 truncate">
                          <span className="font-mono font-semibold">{r.registration_number}</span>
                          <span className="text-muted-foreground"> · {r.supplier_name || 'no supplier'}</span>
                          {r.renewal_po_number && (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`renewal-po-badge-${r.id}`}>
                              Renewal {r.renewal_po_number}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-[10px]">
                            {r.milestone < 0 ? 'expired' : r.milestone === 0 ? 'today' : `T-${r.milestone}d`}
                          </Badge>
                          <span className="text-muted-foreground">
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </Card>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Person Tasks */}
          {(user?.role === 'sales' || user?.role === 'sales_manager') && (
            <>
              {/* Pending Quotations */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Quotations to Approve</h3>
                      <p className="text-sm text-muted-foreground">Requires your action</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800" data-testid="pending-quotations-count">
                    {tasks.pending_quotations.length}
                  </Badge>
                </div>
                {tasks.pending_quotations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No pending quotations</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.pending_quotations.slice(0, 3).map((q) => (
                      <div key={q.id} className="p-3 bg-slate-50 rounded border hover:border-blue-300 cursor-pointer" onClick={() => navigate('/quotations')}>
                        <div className="font-semibold text-sm">{q.quotation_number}</div>
                        <div className="text-xs text-muted-foreground">{q.client_name} - {q.total.toLocaleString()} SAR</div>
                      </div>
                    ))}
                    {tasks.pending_quotations.length > 3 && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/quotations')}>
                        View All ({tasks.pending_quotations.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>

              {/* My Commissions */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">My Commissions</h3>
                      <p className="text-sm text-muted-foreground">Earnings overview</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-sm font-semibold">Eligible</span>
                    <span className="text-lg font-bold text-green-600" data-testid="eligible-commission">
                      {tasks.my_commissions.eligible.toLocaleString()} SAR
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded">
                    <span className="text-sm font-semibold">Pending</span>
                    <span className="text-lg font-bold text-amber-600" data-testid="pending-commission">
                      {tasks.my_commissions.pending.toLocaleString()} SAR
                    </span>
                  </div>
                  <Button size="sm" className="w-full" onClick={() => navigate('/payments')}>
                    View Details
                  </Button>
                </div>
              </Card>
            </>
          )}

          {/* Operations Tasks */}
          {(user?.role === 'operations' || user?.role === 'admin') && (
            <>
              {/* Pending Timesheets */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Timesheets to Approve</h3>
                      <p className="text-sm text-muted-foreground">Awaiting approval</p>
                    </div>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800" data-testid="pending-timesheets-count">
                    {tasks.pending_timesheets.length}
                  </Badge>
                </div>
                {tasks.pending_timesheets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No pending timesheets</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.pending_timesheets.slice(0, 3).map((ts) => (
                      <div key={ts.id} className="p-3 bg-slate-50 rounded border hover:border-purple-300 cursor-pointer" onClick={() => navigate('/timesheets')}>
                        <div className="font-semibold text-sm">{ts.month}</div>
                        <div className="text-xs text-muted-foreground">{ts.hours_worked} hrs - {ts.amount.toLocaleString()} SAR</div>
                      </div>
                    ))}
                    {tasks.pending_timesheets.length > 3 && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/timesheets')}>
                        View All ({tasks.pending_timesheets.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>

              {/* Expiring Documents */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Expiring Documents</h3>
                      <p className="text-sm text-muted-foreground">Urgent renewals</p>
                    </div>
                  </div>
                  <Badge className="bg-red-100 text-red-800" data-testid="expiring-docs-count">
                    {tasks.expiring_documents.length}
                  </Badge>
                </div>
                {tasks.expiring_documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No urgent renewals</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.expiring_documents.slice(0, 3).map((doc) => (
                      <div key={doc.id} className="p-3 bg-red-50 rounded border border-red-200">
                        <div className="font-semibold text-sm">{doc.document_type}</div>
                        <div className="text-xs text-red-600">Expires: {new Date(doc.expiry_date).toLocaleDateString()}</div>
                      </div>
                    ))}
                    {tasks.expiring_documents.length > 3 && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/reports')}>
                        View All ({tasks.expiring_documents.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            </>
          )}

          {/* Finance Tasks */}
          {(user?.role === 'finance' || user?.role === 'admin') && (
            <>
              {/* Pending Invoices */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Receipt className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Invoices to Approve</h3>
                      <p className="text-sm text-muted-foreground">Pending approval</p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800" data-testid="pending-invoices-count">
                    {tasks.pending_invoices.length}
                  </Badge>
                </div>
                {tasks.pending_invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No pending invoices</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.pending_invoices.slice(0, 3).map((inv) => (
                      <div key={inv.id} className="p-3 bg-slate-50 rounded border hover:border-green-300 cursor-pointer" onClick={() => navigate('/invoices')}>
                        <div className="font-semibold text-sm">{inv.invoice_number}</div>
                        <div className="text-xs text-muted-foreground">{inv.client_name} - {inv.total.toLocaleString()} SAR</div>
                      </div>
                    ))}
                    {tasks.pending_invoices.length > 3 && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/invoices')}>
                        View All ({tasks.pending_invoices.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>

              {/* Overdue Payments */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Overdue Payments</h3>
                      <p className="text-sm text-muted-foreground">Requires follow-up</p>
                    </div>
                  </div>
                  <Badge className="bg-red-100 text-red-800" data-testid="overdue-payments-count">
                    {tasks.overdue_payments.length}
                  </Badge>
                </div>
                {tasks.overdue_payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No overdue payments</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.overdue_payments.slice(0, 3).map((inv) => (
                      <div key={inv.id} className="p-3 bg-red-50 rounded border border-red-200">
                        <div className="font-semibold text-sm">{inv.invoice_number}</div>
                        <div className="text-xs text-red-600">{inv.total.toLocaleString()} SAR - Due {new Date(inv.due_date).toLocaleDateString()}</div>
                      </div>
                    ))}
                    {tasks.overdue_payments.length > 3 && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/invoices')}>
                        View All ({tasks.overdue_payments.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            </>
          )}

          {/* Follow-ups */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Bell className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Pending Follow-ups</h3>
                  <p className="text-sm text-muted-foreground">Action required</p>
                </div>
              </div>
              <Badge className="bg-amber-100 text-amber-800" data-testid="pending-followups-count">
                {tasks.pending_follow_ups.length}
              </Badge>
            </div>
            {tasks.pending_follow_ups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending follow-ups</p>
            ) : (
              <div className="space-y-2">
                {tasks.pending_follow_ups.slice(0, 3).map((fu) => (
                  <div key={fu.id} className="p-3 bg-slate-50 rounded border hover:border-amber-300 cursor-pointer" onClick={() => navigate('/follow-ups')}>
                    <div className="font-semibold text-sm text-uppercase">{fu.type} Follow-up</div>
                    <div className="text-xs text-muted-foreground">Due: {new Date(fu.scheduled_date).toLocaleDateString()}</div>
                  </div>
                ))}
                {tasks.pending_follow_ups.length > 3 && (
                  <Button size="sm" variant="outline" className="w-full" onClick={() => navigate('/follow-ups')}>
                    View All ({tasks.pending_follow_ups.length})
                  </Button>
                )}
              </div>
            )}
          </Card>
        </div>
        </>
      )}
    </div>
  );
};
